import type { MouseEvent } from 'react'
import { Button } from '../components/Button.tsx'
import { icons } from '../components/icons.ts'
import type { FindingListItem, FindingsAttentionItem } from '../lib/api.ts'
import { navigate, REPORT_TABS, type ReportTab } from '../lib/route.ts'
import { ConferenceTime } from './ConferenceTime.tsx'
import { FindingsReport } from './FindingsReport.tsx'

/**
 * Relatórios (#551): the numbers we read about the product, a tab each.
 *
 * One menu entry rather than one per report, so the next report is a tab and
 * not another line in Ferramentas. Tabs navigate rather than swap state, as on
 * the golden set, so each report has an address that survives a reload and can
 * be sent to somebody.
 */
const TAB_LABEL: Readonly<Record<ReportTab, string>> = {
  findings: 'Apontamentos',
  'conference-time': 'Tempo de conferência',
}

export function Reports({
  tab,
  sessionAccountId,
  onBack,
  onOpenVerification,
  onOpenAttentionVerification,
}: {
  tab: ReportTab
  /** The account the session is in: where Tempo de conferência opens (#640). */
  sessionAccountId?: string | undefined
  onBack: () => void
  onOpenVerification: (item: FindingListItem, event: MouseEvent<HTMLAnchorElement>) => void
  onOpenAttentionVerification: (
    item: FindingsAttentionItem,
    event: MouseEvent<HTMLAnchorElement>,
  ) => void
}) {
  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Relatórios</h1>
      </div>

      <div className="border-stroke-weak flex gap-1 border-b" role="tablist">
        {REPORT_TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => navigate({ name: 'reports', tab: key })}
            className={
              tab === key
                ? 'text-tPSemi text-text-brand border-fill-brand -mb-px border-b-2 px-3 py-2'
                : 'text-tP text-text-weak hover:text-text-strong -mb-px border-b-2 border-transparent px-3 py-2'
            }
          >
            {TAB_LABEL[key]}
          </button>
        ))}
      </div>

      {tab === 'conference-time' ? (
        <ConferenceTime sessionAccountId={sessionAccountId} />
      ) : (
        <FindingsReport
          onOpenVerification={onOpenVerification}
          onOpenAttentionVerification={onOpenAttentionVerification}
        />
      )}
    </div>
  )
}
